import CTASection from "../components/CTASection";
import RiskWarning from "../components/RiskWarning";
import { SIF_MIN_INVESTMENT } from "../constants/compliance";
import { AlertTriangle, Shield, TrendingUp } from "lucide-react";

export default function SIF() {
  return (
    <div id="main-content" className="pt-20" data-testid="sif-page">
      <section className="py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8 p-4 bg-[#1a1410]/30 border border-[#8b7355]/50 rounded-lg" data-testid="sif-notice">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-[#d4af69] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-[#d4af69] mb-2">AMFI Registered SIF Distributor</h3>
                <p className="text-sm text-gray-300">
                  This section contains information about Specialized Investment Funds (SIF), which are distinct from regular mutual funds.
                </p>
              </div>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-gray-100" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Specialized Investment Funds
          </h1>
          <p className="text-lg text-gray-400 mb-12">
            Kredere Family Office is registered with AMFI to distribute Specialized Investment Funds (SIF) to eligible investors.
          </p>

          <div className="space-y-8">
            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg" data-testid="sif-differentiation">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">What Makes SIF Different?</h2>
              <p className="text-gray-400 mb-4">
                <strong className="text-gray-200">SIF is distinct from regular mutual funds</strong> in terms of structure, regulation, and investor eligibility.
              </p>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>SIF operates under a different regulatory framework compared to traditional mutual funds</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>Investment strategies and asset classes may differ from conventional mutual fund offerings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>Eligibility criteria and disclosure requirements are specific to SIF</span>
                </li>
              </ul>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg" data-testid="sif-eligibility">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Minimum Investment Requirement</h2>
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-[#d4af69] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-300 font-medium mb-2">
                    Minimum investment under SIF is <strong className="text-[#d4af69]">{SIF_MIN_INVESTMENT}</strong> across strategies
                  </p>
                  <p className="text-sm text-gray-400">
                    (except for accredited investors, as applicable under regulatory provisions)
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg" data-testid="sif-distribution-role">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Our Role in SIF Distribution</h2>
              <p className="text-gray-400 mb-4">
                As an AMFI-registered SIF distributor, Kredere Family Office:
              </p>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-[#d4af69] flex-shrink-0 mt-0.5" />
                  <span>Facilitates access to SIF products for eligible investors</span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-[#d4af69] flex-shrink-0 mt-0.5" />
                  <span>Provides factual information about SIF structures and requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-[#d4af69] flex-shrink-0 mt-0.5" />
                  <span>Ensures proper disclosure and documentation as per regulatory standards</span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-[#d4af69] flex-shrink-0 mt-0.5" />
                  <span>Does not provide investment advisory services unless separately registered</span>
                </li>
              </ul>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg" data-testid="sif-documentation">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Documentation & Disclosure</h2>
              <p className="text-gray-400 mb-4">
                All SIF investments require thorough review of official documents including:
              </p>
              <ul className="space-y-2 text-gray-400 text-sm ml-6">
                <li className="list-disc">Information Statement and Investment Details (ISID)</li>
                <li className="list-disc">Offer documents and disclosure materials provided by the SIF/AMC</li>
                <li className="list-disc">Risk factors and investment restrictions</li>
                <li className="list-disc">Fee structure and expense disclosures</li>
              </ul>
              <p className="text-gray-400 mt-4 text-sm">
                Investors are advised to carefully read all scheme-related documents before making any investment decisions.
              </p>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg" data-testid="sif-important-notes">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Important Notes</h2>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>SIF investments carry specific risks that may differ from traditional mutual funds</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>Past performance is not indicative of future returns</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>Information provided is for educational purposes only and not investment advice</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>Eligibility criteria must be satisfied before investment</span>
                </li>
              </ul>
            </div>

            <CTASection />
          </div>
        </div>
      </section>
    </div>
  );
}